/**
 * Image Upload Component
 * Reusable image upload with preview for profiles and portfolios
 */

import { ImagePlus, Loader2, Upload, X } from "lucide-react";
import { useRef, useState } from "react";
import { AvatarCropDialog } from "@/components/ui/avatar-crop-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn, resolveMediaUrl } from "@/lib/utils";
import { uploadFile, uploadMultipleFiles } from "@/services/upload.service";

interface SingleImageUploadProps {
	label?: string;
	currentUrl?: string;
	onUpload: (url: string) => void;
	onRemove?: () => void;
	className?: string;
	/** Enable LinkedIn-style crop & reposition dialog */
	enableCrop?: boolean;
	/**
	 * Told when an upload starts and when it ends, so a parent can hold off
	 * saving. Not called for the `enableCrop` path, whose dialog uploads on
	 * its own.
	 */
	onUploadingChange?: (uploading: boolean) => void;
}

export function SingleImageUpload({
	label = "Upload Image",
	currentUrl,
	onUpload,
	onRemove,
	className,
	enableCrop = false,
	onUploadingChange,
}: SingleImageUploadProps) {
	const [isUploading, setIsUploading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
	const [cropDialogOpen, setCropDialogOpen] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		if (!file.type.startsWith("image/")) {
			setError("Please select an image file");
			return;
		}

		if (file.size > 10 * 1024 * 1024) {
			setError("Image must be less than 10MB");
			return;
		}

		setError(null);

		if (enableCrop) {
			// Read file as data URL and open the crop dialog
			const reader = new FileReader();
			reader.onload = () => {
				setCropImageSrc(reader.result as string);
				setCropDialogOpen(true);
			};
			reader.onerror = () => {
				setError("Failed to read image file");
			};
			reader.readAsDataURL(file);
		} else {
			// Upload directly
			setIsUploading(true);
			onUploadingChange?.(true);
			try {
				const result = await uploadFile(file);

				if (result.success && result.url) {
					onUpload(result.url);
				} else {
					setError(result.error || "Upload failed");
				}
			} catch {
				setError("Upload failed");
			} finally {
				setIsUploading(false);
				onUploadingChange?.(false);
			}
		}

		if (inputRef.current) inputRef.current.value = "";
	};

	return (
		<div className={cn("space-y-2", className)}>
			{label && (
				<p
					style={{
						fontFamily: "var(--font-mono)",
						fontSize: 11,
						fontWeight: 600,
						letterSpacing: "0.15em",
						textTransform: "uppercase",
						color: "var(--color-dashboard-muted)",
					}}
				>
					{label}
				</p>
			)}

			{currentUrl ? (
				<div className="relative inline-block">
					<img
						src={resolveMediaUrl(currentUrl)}
						alt="Uploaded"
						className="h-32 w-32 rounded-lg object-cover border"
					/>
					{onRemove && (
						<Button
							type="button"
							aria-label="Remove image"
							variant="destructive"
							size="icon"
							className="absolute -right-2 -top-2 h-6 w-6 rounded-full"
							onClick={onRemove}
						>
							<X className="h-3 w-3" />
						</Button>
					)}
				</div>
			) : (
				<Card
					className={cn(
						"cursor-pointer border-dashed border-dashboard-border hover:border-dashboard-accent/50 transition-colors bg-dashboard-bg",
						isUploading && "pointer-events-none opacity-60",
					)}
					onClick={() => inputRef.current?.click()}
				>
					<CardContent className="flex flex-col items-center justify-center p-6">
						{isUploading ? (
							<Loader2 className="h-8 w-8 animate-spin text-dashboard-muted" />
						) : (
							<Upload className="h-8 w-8 text-dashboard-muted" />
						)}
						<p className="mt-2 text-sm text-dashboard-muted">
							{isUploading ? "Uploading..." : "Click to upload"}
						</p>
					</CardContent>
				</Card>
			)}

			<input
				ref={inputRef}
				type="file"
				accept="image/jpeg,image/png,image/webp"
				className="hidden"
				onChange={handleFileSelect}
			/>

			{error && <p className="text-sm text-destructive">{error}</p>}

			{/* Avatar Crop Dialog */}
			<AvatarCropDialog
				open={cropDialogOpen}
				onOpenChange={setCropDialogOpen}
				imageSrc={cropImageSrc}
				onUpload={onUpload}
			/>
		</div>
	);
}

interface MultiImageUploadProps {
	label?: string;
	currentUrls: string[];
	maxImages?: number;
	onUpload: (urls: string[]) => void;
	onRemove: (index: number) => void;
	className?: string;
	/** Told when an upload starts and when it ends, so a parent can hold off saving. */
	onUploadingChange?: (uploading: boolean) => void;
}

export function MultiImageUpload({
	label = "Upload Images",
	currentUrls,
	maxImages = 10,
	onUpload,
	onRemove,
	className,
	onUploadingChange,
}: MultiImageUploadProps) {
	const [isUploading, setIsUploading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const handleFilesSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files || []);
		if (files.length === 0) return;

		const remaining = maxImages - currentUrls.length;
		if (files.length > remaining) {
			setError(
				`You can only add ${remaining} more image${remaining === 1 ? "" : "s"}`,
			);
			return;
		}

		const invalidFiles = files.filter((f) => !f.type.startsWith("image/"));
		if (invalidFiles.length > 0) {
			setError("Please select only image files");
			return;
		}

		const oversizedFiles = files.filter((f) => f.size > 10 * 1024 * 1024);
		if (oversizedFiles.length > 0) {
			setError("Each image must be less than 10MB");
			return;
		}

		setError(null);
		setIsUploading(true);
		onUploadingChange?.(true);
		try {
			const result = await uploadMultipleFiles(files);

			if (result.success && result.urls) {
				onUpload(result.urls);
			} else {
				setError(result.error || "Upload failed");
			}
		} catch {
			setError("Upload failed");
		} finally {
			setIsUploading(false);
			onUploadingChange?.(false);
		}

		if (inputRef.current) inputRef.current.value = "";
	};

	const canAddMore = currentUrls.length < maxImages;

	return (
		<div className={cn("space-y-2", className)}>
			{label && (
				<p
					style={{
						fontFamily: "var(--font-mono)",
						fontSize: 11,
						fontWeight: 600,
						letterSpacing: "0.15em",
						textTransform: "uppercase",
						color: "var(--color-dashboard-muted)",
					}}
				>
					{label} ({currentUrls.length}/{maxImages})
				</p>
			)}

			<div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
				{currentUrls.map((url, index) => (
					<div key={url} className="group relative aspect-square">
						<img
							src={resolveMediaUrl(url)}
							alt={`Image ${index + 1}`}
							className="h-full w-full rounded-lg object-cover border"
						/>
						<Button
							type="button"
							aria-label="Remove image"
							variant="destructive"
							size="icon"
							className="absolute -right-1 -top-1 h-5 w-5 rounded-full opacity-0 transition-opacity group-hover:opacity-100"
							onClick={() => onRemove(index)}
						>
							<X className="h-3 w-3" />
						</Button>
					</div>
				))}

				{canAddMore && (
					<Card
						className={cn(
							"aspect-square cursor-pointer border-dashed border-dashboard-border hover:border-dashboard-accent/50 transition-colors bg-dashboard-bg",
							isUploading && "pointer-events-none opacity-60",
						)}
						onClick={() => inputRef.current?.click()}
					>
						<CardContent className="flex h-full flex-col items-center justify-center p-2">
							{isUploading ? (
								<Loader2 className="h-6 w-6 animate-spin text-dashboard-muted" />
							) : (
								<ImagePlus className="h-6 w-6 text-dashboard-muted" />
							)}
							<p className="mt-1 text-xs text-dashboard-muted text-center">
								{isUploading ? "Uploading..." : "Add"}
							</p>
						</CardContent>
					</Card>
				)}
			</div>

			<input
				ref={inputRef}
				type="file"
				accept="image/jpeg,image/png,image/webp"
				multiple
				className="hidden"
				onChange={handleFilesSelect}
			/>

			{error && <p className="text-sm text-destructive">{error}</p>}
		</div>
	);
}
